/**
 * Flag-driven styling for {@link DayCell}. A day can be in several states at
 * once (today + selected, range-start + selected …) so we resolve from booleans
 * rather than a single enum. Selection wins over range/today for the fill.
 *
 * Tokens mirror the Figma redesign (AOS-619):
 * - selected / range endpoint → `action.default` fill, `aos-text.on-primary` ink
 * - anything else             → transparent, so the cell blends with whatever
 *   the parent paints (the panel background, or the row-level range band)
 * - today (unselected)        → `action.default` outline
 * - outside-month             → muted text
 * - disabled                  → muted text + reduced opacity, non-interactive
 */
export type DayCellFlags = {
    isSelected?: boolean
    isToday?: boolean
    isOutsideMonth?: boolean
    /**
     * Kept for call-site compatibility — the fill no longer branches on it now
     * that unselected cells are transparent either way. The range band is drawn
     * by the parent row; endpoints resolve via `isRangeStart`/`isRangeEnd`.
     */
    isInRange?: boolean
    isRangeStart?: boolean
    isRangeEnd?: boolean
    isDisabled?: boolean
    /** Whether the cell reacts to hover/press (an `onPress` was provided). */
    interactive?: boolean
}

export type DayCellClasses = {
    container: string
    text: string
}

export const dayCellVariants = ({
    isSelected,
    isToday,
    isOutsideMonth,
    isRangeStart,
    isRangeEnd,
    isDisabled,
    interactive,
}: DayCellFlags): DayCellClasses => {
    const selected = Boolean(isSelected || isRangeStart || isRangeEnd)

    const background = selected ? 'bg-action-default' : 'bg-transparent'

    const container = [
        'size-6 items-center justify-center rounded',
        background,
        isToday && !selected ? 'border border-action-default' : '',
        isDisabled ? 'opacity-40' : '',
        interactive && !isDisabled && !selected ? 'hover:bg-aos-bg-elevation-hover' : '',
    ]
        .filter(Boolean)
        .join(' ')

    const textTone = isDisabled
        ? 'text-aos-text-disabled'
        : selected
          ? 'text-aos-text-on-primary'
          : isOutsideMonth
            ? 'text-aos-text-muted'
            : 'text-aos-text-default'

    return { container, text: textTone }
}
